import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FirewallBlockedError, UserCancelledError, formatBytes, type FileSizeCheck } from "@/lib/ffmpegEngine";
import { Square, AlertTriangle } from "lucide-react";
import { usePipelineContext } from "@/lib/pipelineContext";
import { NoticeBox } from "@/components/NoticeBox";

/** Where this tool sits in a bundle run: `intermediate` is true for every step except the last. */
export function useAudioPipelineStep(): { inPipeline: boolean; intermediate: boolean } {
  const p = usePipelineContext();
  return { inPipeline: !!p, intermediate: !!p && p.currentStepIndex < p.steps.length - 1 };
}

export function AudioProcessingProgress({
  progress,
  statusMessage,
  onStop,
}: {
  progress: number;
  statusMessage: string;
  onStop?: () => void;
}) {
  const pct = Math.max(0, Math.min(100, Math.round(progress * 100)));
  return (
    <div className="space-y-2">
      <Progress value={pct} aria-label="Progress" />
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">{statusMessage || "Processing..."}{pct > 0 && pct < 100 ? ` — ${pct}%` : ""}</p>
        {onStop && (
          <Button variant="outline" size="sm" onClick={onStop} className="gap-1.5 shrink-0 h-7">
            <Square className="h-3 w-3" /> Stop
          </Button>
        )}
      </div>
    </div>
  );
}

export function showAudioError(err: unknown) {
  if (err instanceof FirewallBlockedError) {
    toast.error("Couldn't load the audio engine — a firewall or restrictive network may be blocking it. Try a different network, or check with your IT team if you're on a work or school connection.");
  } else if (err instanceof UserCancelledError) {
    toast.info("Processing stopped.");
  } else if (err instanceof Error && /memory access out of bounds|RuntimeError/i.test(err.message)) {
    console.error("Audio processing failed (WASM runtime error):", err);
    toast.error("The audio engine ran into an internal error processing this file. Try again (the engine has been reset), or try a smaller file if it keeps happening.");
  } else {
    console.error("Audio processing failed:", err);
    toast.error("Something went wrong processing this audio file. Check the browser console (F12) for the specific error, or try a different file.");
  }
}

export { UNREADABLE_AUDIO_MESSAGE, NO_AUDIO_TRACK_MESSAGE } from "./messages";

export function FileSizeNotice({ check, isPro }: { check: FileSizeCheck; isPro: boolean }) {
  if (check.severity === "ok") return null;

  if (check.severity === "over-hard-limit") {
    return (
      <div className="flex gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/5">
        <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
        <p className="text-sm text-destructive">
          This file ({formatBytes(check.totalBytes)}) is too large to be processed here — the limit is{" "}
          {formatBytes(check.proMaxBytes)}, even on Pro. Try a smaller file.
        </p>
      </div>
    );
  }

  return (
    <NoticeBox className="flex gap-2">
      <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
      <p className="text-sm">
        You've selected a file over {formatBytes(check.freeMaxBytes)} ({formatBytes(check.totalBytes)}) — trial use
        supports up to {formatBytes(check.freeMaxBytes)}. Upgrade to Pro for up to {formatBytes(check.proMaxBytes)}.
      </p>
    </NoticeBox>
  );
}
